import { isDualCpuFlashQuote } from './flashNodeModel';

/**
 * Master kill switch for the entire CCI feature.
 *
 * Set to false to fully disable the CCI quote path and hide the Standard/CCI
 * toggle for everyone, in every environment — regardless of vendor, admin
 * access, or a quote's persisted cciEnabled flag. This is a complete rollback
 * on its own: `cciEnabled: true` on a config is inert while this is off.
 *
 * Enabled 2026-08-03. Kept as a constant rather than an env var deliberately,
 * so rollback is a one-line revert. See the 2026-08-03 CCI enablement spec.
 */
export const CCI_FEATURE_ENABLED = true;

/**
 * Decides whether the CCI quote path should run.
 * CCI is off until an admin (specialPricing group) explicitly enables it
 * via the per-quote toggle, and only applies to the AIC vendor.
 *
 * @param {Object} params
 * @param {string}  params.vendorId         - active vendor id (e.g. 'AIC')
 * @param {boolean} params.cciEnabled       - per-quote config.cciEnabled flag
 * @param {boolean} params.hasSpecialAccess - user is in the specialPricing group
 * @param {boolean} [params.dualCpuFlash]   - CONF-210: the quote ships Dual CPU
 *   flash nodes, which no CCI bundle covers; closes the path when true
 * @returns {boolean}
 */
export function shouldUseCciPath({ vendorId, cciEnabled, hasSpecialAccess, dualCpuFlash = false } = {}) {
  return vendorId === 'AIC' && cciEnabled === true && hasSpecialAccess === true && dualCpuFlash !== true;
}

/**
 * CONF-210. Why the CCI path is closed for this config, as UI copy, or null
 * when it is not closed for this reason. cciSkuMapping.json has no row for the
 * Dual CPU flash node's OCP + 2x PCIe build, so rather than let the lookup
 * fall through to cci_fallback silently at the point of sale, the toggle is
 * disabled with this text and the audit log records `dual_cpu_flash`.
 */
export function cciBlockedByDualCpuReason(config) {
  return isDualCpuFlashQuote(config)
    ? 'CCI bundles do not cover the Dual CPU flash node (VCH-5500-F1N); the standard quote path will be used.'
    : null;
}
